import { useEffect, useState } from "react"
import { arrayOf, bool, number, shape, string, func } from "prop-types"
import { colors } from "components/shared/admin_colors"
import { Box, Button, ToggleSwitch, Tooltip } from "@planningcenter/tapestry-react"
import { Loading } from "components/ui"
import {
  SettingPane,
  useSettings,
  SettingContextProvider
} from "components/admin/settingPanes/SettingPane"
import useApiResource from "hooks/use_api_resource"
import Reminders from "./Reminders"
import RemindersForm from "./RemindersForm"

const SCHEDULABLE_EMAIL_FIELDS =
  "duration_number, duration_unit, duration_unit_options, duration_string, message, sent_at_time, sent_at_date, send_at_time, send_at_date, attendee_types"

const GeneralReminders = ({
  loading,
  event,
  reminders: initialReminders,
  attendeeTypes,
  onMutate,
  onDestroy,
  onFetch
}) => {
  const api = useApiResource({ path: "events" })

  const {
    isEditing,
    isSaving,
    onSaving,
    onLoading,
    onSuccess,
    error,
    onError,
    onEditing,
    onCancel
  } = useSettings()

  const [isGeneralRemindersEnabled, setIsGeneralRemindersEnabled] = useState(
    event.generalRemindersEnabled || false
  )

  const [reminders, setReminders] = useState([])

  useEffect(() => {
    onLoading(loading)
  }, [loading])

  useEffect(() => {
    if (!initialReminders || !attendeeTypes) return
    resetReminders()
  }, [initialReminders, attendeeTypes])

  const isToggleDisabled = event.startsInThePast || !event.hasEventTimes || event.hasSeparateTimes

  const resetReminders = () => {
    const activeReminders = initialReminders
      .map(ir => {
        const attendeeTypeNames =
          ir.attendeeTypes.length > 0
            ? ir.attendeeTypes
                .map(id => attendeeTypes.find(type => type.id === id)?.name)
                .filter(Boolean)
                .join(", ")
            : "all attendees"

        return { ...ir, key: ir.id, attendeeTypeNames }
      })
      .filter(ir => ir.attendeeTypeNames)

    setReminders(activeReminders)
  }

  const handleToggle = async () => {
    if (isToggleDisabled) return onError("Reminders are not available for this event.")

    onEditing(false)
    onSaving()

    try {
      const response = await api.run(`${event.id}/toggle_reminder`, {
        fields: {
          Event: "name, general_reminders_enabled, schedulable_emails",
          SchedulableEmail: SCHEDULABLE_EMAIL_FIELDS
        },
        include: "schedulable_emails"
      })

      onSuccess()
      setIsGeneralRemindersEnabled(!isGeneralRemindersEnabled)
      setReminders(response.data.schedulableEmails || [])
    } catch (error) {
      onError(error.errors ? error.errors[0].detail : "Something went wrong")
    }
  }

  const handleFieldChange = reminder => {
    setReminders(stateReminders =>
      stateReminders.map(r => (r.key === reminder.key ? { ...r, ...reminder } : r))
    )
  }

  const handleReminderChange = (reminder, status = null) => {
    if (status === "new") return setReminders([...reminders, reminder])
    if (status === "delete") return setReminders(reminders.filter(r => r.key !== reminder.key))

    setReminders(reminders.filter(r => r.key === reminder.key).map(r => ({ ...r, ...reminder })))
  }

  const handleCancel = () => {
    resetReminders()
    onCancel()
  }

  const handleSave = async () => {
    if (reminders.length > 1 && reminders.some(r => r.attendeeTypes.length === 0)) {
      return onError("You must select at least one attendee type for each reminder.")
    }

    onSaving()

    const remindersToDelete = initialReminders
      .map(ir => {
        const reminder = reminders.find(r => r.id === ir.id)
        return reminder ? null : { ...ir, _destroy: true }
      })
      .filter(Boolean)

    const response = await Promise.all(
      [...remindersToDelete, ...reminders].map(r => {
        if (r._destroy) {
          return onDestroy(r.id)
        }

        const attributes = {
          duration_number: r.durationNumber,
          duration_unit: r.durationUnit,
          message: r.message,
          attendee_types: r.attendeeTypes,
          ...(r.id && { id: r.id })
        }

        return onMutate(attributes, {
          fields: {
            SchedulableEmail: SCHEDULABLE_EMAIL_FIELDS
          }
        })
      })
    )

    const error = response.find(r => r.errors)

    if (error) {
      onError(error.errors[0].detail)
    } else {
      await onFetch()
      onSuccess()
    }
  }

  let tooltipText = ""

  if (event.startsInThePast) {
    tooltipText = "Reminders are not available for events that have already started."
  } else if (!event.hasEventTimes) {
    tooltipText = "Reminders are not available for events without times."
  } else if (event.hasSeparateTimes) {
    tooltipText = "Reminders are not available for events with separate times enabled."
  }

  return (
    <SettingPane
      onSave={handleSave}
      onCancel={handleCancel}
      title={
        <Tooltip placement="top" title={tooltipText}>
          <Box>
            <ToggleSwitch
              label="General Reminders"
              disabled={isToggleDisabled || loading || isSaving}
              isChecked={isGeneralRemindersEnabled}
              onClick={handleToggle}
            />
          </Box>
        </Tooltip>
      }
      blankState={
        !loading &&
        !isGeneralRemindersEnabled && (
          <>
            {!isSaving && error && <div style={{ color: colors.error }}>{error}</div>}
            <Box marginBottom={1}>
              Enable General Reminders to keep your event top-of-mind for attendees to maximize
              attendance.
            </Box>
            <Tooltip placement="top" title={tooltipText}>
              <Button
                title="Enable general reminders"
                size="sm"
                theme="app"
                onClick={handleToggle}
                disabled={isToggleDisabled || loading || isSaving}
              />
            </Tooltip>
          </>
        )
      }
    >
      {loading && <Loading />}
      {!loading && isEditing && (
        <RemindersForm
          reminders={reminders}
          attendeeTypes={attendeeTypes}
          onFieldChange={handleFieldChange}
          onChange={handleReminderChange}
        />
      )}
      {!loading && !isEditing && <Reminders reminders={reminders} />}
    </SettingPane>
  )
}

GeneralReminders.propTypes = {
  loading: bool,
  event: shape({
    id: number.isRequired,
    generalRemindersEnabled: bool.isRequired,
    hasEventTimes: bool.isRequired,
    hasSeparateTimes: bool.isRequired
  }),
  reminders: arrayOf(
    shape({
      id: number
    })
  ),
  attendeeTypes: arrayOf(
    shape({
      id: number,
      name: string
    })
  ),
  onMutate: func,
  onDestroy: func,
  onFetch: func
}

const GeneralRemindersWrapper = props => (
  <SettingContextProvider>
    <GeneralReminders {...props} />
  </SettingContextProvider>
)

export default GeneralRemindersWrapper
